import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Building2, FileSearch, Lock, ArrowRight } from "lucide-react";
import { Section, Overline, Reveal } from "@/components/Primitives";
import { Seo } from "@/components/Seo";
import { TrustSection, SUNBIZ_URL } from "@/components/TrustSection";
import { FlightTrail } from "@/components/FlightTrail";

const PILLARS = [
  {
    icon: Building2,
    title: "Une entreprise réelle et enregistrée",
    text: "Work Travel Expert L.L.C. est enregistrée auprès du Florida Department of State sous le numéro de document L26000334420, avec un statut actif.",
  },
  {
    icon: FileSearch,
    title: "Une vérification indépendante",
    text: "Vous n'avez pas à nous croire sur parole : consultez notre certificat officiel et vérifiez nos informations publiques sur le registre de l'État de Floride (Sunbiz).",
  },
  {
    icon: Lock,
    title: "Des paiements sécurisés",
    text: "Tous les règlements sont traités par Stripe avec un chiffrement de niveau bancaire. Nous n'avons jamais accès à vos données de carte.",
  },
];

export default function Verified() {
  return (
    <div data-testid="verified-page">
      <Seo
        title="Entreprise vérifiée — Work Travel Expert L.L.C., société enregistrée en Floride (USA)"
        description="Work Travel Expert L.L.C. est une entreprise officiellement enregistrée dans l'État de Floride (USA). Consultez notre Certificate of Status officiel et vérifiez nos informations sur le registre Sunbiz."
        keywords="work travel expert llc, entreprise vérifiée, certificate of status floride, sunbiz, société enregistrée usa"
        path="/entreprise-verifiee"
      />

      {/* HERO */}
      <div className="relative bg-navy overflow-hidden">
        <div className="pointer-events-none absolute -top-40 -right-24 w-[34rem] h-[34rem] rounded-full bg-gold/15 blur-[120px] animate-float-slow" />
        <Section className="relative z-10 py-20 sm:py-24">
          <Reveal>
            <Overline>Transparence &amp; confiance</Overline>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-medium text-white max-w-3xl leading-[1.08]">
              Une entreprise que vous pouvez <span className="text-gold">vérifier</span>
            </h1>
            <div className="gold-line mt-6" />
            <p className="mt-6 max-w-2xl text-white/80 leading-relaxed">
              Confier son projet de vie à une agence demande de la confiance. C'est pourquoi nous rendons publiques les informations
              officielles de notre entreprise : vous pouvez consulter notre certificat et vérifier notre existence par vous-même.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-2 text-xs uppercase tracking-[0.18em] text-gold">
                <ShieldCheck size={14} /> Statut : Active
              </span>
              <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/[0.06] px-4 py-2 text-xs uppercase tracking-[0.18em] text-white/80">
                Document n° L26000334420
              </span>
            </div>
          </Reveal>
          <FlightTrail className="mt-10 max-w-3xl" />
        </Section>
      </div>

      {/* CERTIFICAT + BOUTONS DE VÉRIFICATION */}
      <TrustSection theme="light" />

      {/* PILIERS DE CONFIANCE */}
      <div className="bg-[#F8F9FA]">
        <Section className="py-20">
          <div className="grid md:grid-cols-3 gap-6">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.1}>
                <div
                  data-testid={`verified-pillar-${i}`}
                  className="h-full bg-white border border-border p-8 transition-all duration-300 hover:-translate-y-1 hover:border-gold hover:shadow-xl"
                >
                  <div className="h-12 w-12 bg-navy text-gold flex items-center justify-center">
                    <p.icon size={22} />
                  </div>
                  <h2 className="font-heading text-2xl text-navy mt-5">{p.title}</h2>
                  <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.15}>
            <div className="mt-12 rounded-2xl bg-navy text-white p-8 sm:p-10 flex flex-col lg:flex-row lg:items-center gap-6 justify-between">
              <div>
                <h2 className="font-heading text-2xl sm:text-3xl">Prêt à démarrer votre projet en toute confiance ?</h2>
                <p className="mt-3 text-white/75 leading-relaxed max-w-2xl">
                  Découvrez nos formules d'accompagnement ou réservez une consultation gratuite avec un conseiller.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 shrink-0">
                <Link
                  to="/nos-packs"
                  data-testid="verified-cta-packs"
                  className="group inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-hover text-white px-7 py-3.5 font-medium rounded-lg transition-colors"
                >
                  Voir nos packs <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" />
                </Link>
                <a
                  href={SUNBIZ_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="verified-cta-sunbiz"
                  className="inline-flex items-center justify-center gap-2 border border-white/25 text-white hover:border-gold hover:text-gold px-7 py-3.5 font-medium rounded-lg transition-colors"
                >
                  Vérifier sur Sunbiz
                </a>
              </div>
            </div>
          </Reveal>
        </Section>
      </div>
    </div>
  );
}
